import { ISO_DATE_FORMAT, dayjsEn } from '@bemlo/date'
import { EmploymentType } from '@bemlo/enums'

import { Combobox, Flex, IconName } from '../../../elements'
import { DatePicker } from '../../../elements/Form/DatePicker'
import { useTranslations } from '../../../translate'
import { Detail } from '../components/Detail'
import { useCandidateView } from '../hooks/useCandidateView'
import { scopes } from '../scopes'

const SELECTABLE_EMPLOYMENT_TYPES = [
  EmploymentType.PERMANENT,
  EmploymentType.HOURLY,
]

export const Employment = () => {
  const t = useTranslations()
  const { actor, data, isEditing, patchEditData } = useCandidateView()
  const { contract } = data

  const scope = scopes.contract()

  if (!actor.can('read', scope) || !contract) {
    return null
  }

  const label = t.candidate_view.contract.employment_type()
  const fromLabel = t.candidate_view.contract.from()
  const untilLabel = t.candidate_view.contract.until()
  const icon: IconName = 'CheckIcon'

  if (isEditing(scope)) {
    return (
      <Flex gap={3} wrap>
        <Combobox
          disabled={!actor.can('update', scope)}
          label={label}
          labelIcon={icon}
          onChange={(employment) =>
            employment &&
            patchEditData({ contract: { ...contract, employment } })
          }
          options={SELECTABLE_EMPLOYMENT_TYPES.map((value) => ({
            label: t.$employmentType(value),
            value,
            key: value,
          }))}
          value={contract.employment}
        />
        <DatePicker
          label={fromLabel}
          labelIcon="CalendarIcon"
          inputStyles={{ width: 160, minWidth: 0 }}
          onChange={(startDate) =>
            patchEditData({
              contract: {
                ...contract,
                startsAt: startDate ? t.$date(startDate).unix() : null,
              },
            })
          }
          value={
            contract.startsAt
              ? t.$date.unix(contract.startsAt).toDate()
              : undefined
          }
          maxDate={
            contract.endsAt ? t.$date.unix(contract.endsAt).toDate() : undefined
          }
        />
        <DatePicker
          label={untilLabel}
          labelIcon="CalendarIcon"
          inputStyles={{ width: 160, minWidth: 0 }}
          onChange={(endsAt) =>
            patchEditData({
              contract: {
                ...contract,
                endsAt: endsAt ? t.$date(endsAt).unix() : null,
              },
            })
          }
          value={
            contract.endsAt ? t.$date.unix(contract.endsAt).toDate() : undefined
          }
          minDate={
            contract.startsAt
              ? t.$date.unix(contract.startsAt).toDate()
              : undefined
          }
        />
      </Flex>
    )
  }

  const startsAt = contract.startsAt
    ? dayjsEn.unix(contract.startsAt).format(ISO_DATE_FORMAT)
    : null
  const endsAt = contract.endsAt
    ? dayjsEn.unix(contract.endsAt).format(ISO_DATE_FORMAT)
    : null

  const period =
    startsAt && endsAt
      ? `${startsAt} - ${endsAt}`
      : startsAt
        ? `${fromLabel} ${startsAt}`
        : endsAt
          ? `${untilLabel} ${endsAt}`
          : ''

  const content = [t.$employmentType(contract.employment), period].join(', ')

  return <Detail content={content} heading={label} icon={icon} />
}
